// src/components/admin/compensation/PayGroupModal.js
// Modal for creating/editing pay groups with salary component templates

import React, { useState, useEffect } from 'react';
import {  X, Plus, Trash2, DollarSign, Percent, AlertCircle } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import payGroupsService from '../../../services/payGroups.service';

const PayGroupModal = ({ isOpen, onClose, onSave, payGroup, mode = 'create' }) => {
    const { theme } = useTheme();
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [showCascadeDialog, setShowCascadeDialog] = useState(false);
    const [cascadeUpdate, setCascadeUpdate] = useState(false);
    const [employeeCount, setEmployeeCount] = useState(0);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        level: '',
        description: '',
        min_ctc: '',
        max_ctc: '',
        currency: 'INR',
        salary_components: [],
        is_active: true
    });

    useEffect(() => {
        if (payGroup && mode === 'edit') {
            setFormData({
                name: payGroup.name || '',
                level: payGroup.level || '',
                description: payGroup.description || '',
                min_ctc: payGroup.min_ctc || '',
                max_ctc: payGroup.max_ctc || '',
                currency: payGroup.currency || 'INR',
                salary_components: payGroup.salary_components || [],
                is_active: payGroup.is_active !== undefined ? payGroup.is_active : true
            });

            // Fetch employee count for this pay group
            if (payGroup.id) {
                payGroupsService.getPayGroupEmployees(payGroup.id)
                    .then(response => {
                        if (response.data && response.data.employees) {
                            setEmployeeCount(response.data.employees.length);
                        }
                    })
                    .catch(error => {
                        console.error('Error fetching employee count:', error);
                        setEmployeeCount(0);
                    });
            }
        }
    }, [payGroup, mode]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name?.trim()) {
            newErrors.name = 'Pay group name is required';
        }

        if (!formData.level?.trim()) {
            newErrors.level = 'Level is required';
        }

        if (!formData.min_ctc || parseFloat(formData.min_ctc) <= 0) {
            newErrors.min_ctc = 'Minimum CTC must be greater than 0';
        }

        if (!formData.max_ctc || parseFloat(formData.max_ctc) <= 0) {
            newErrors.max_ctc = 'Maximum CTC must be greater than 0';
        }

        if (parseFloat(formData.min_ctc) > parseFloat(formData.max_ctc)) {
            newErrors.ctc_range = 'Minimum CTC cannot be greater than maximum CTC';
        }

        // Validate salary components
        if (formData.salary_components.length > 0) {
            const totalPercentage = formData.salary_components.reduce(
                (sum, comp) => sum + parseFloat(comp.percentage || 0),
                0
            );

            if (Math.abs(totalPercentage - 100) > 0.01) {
                newErrors.components = `Total percentage must equal 100% (current: ${totalPercentage.toFixed(1)}%)`;
            }

            // Check for duplicate names
            const names = formData.salary_components.map(c => c.name.toLowerCase());
            const duplicates = names.filter((name, index) => names.indexOf(name) !== index);
            if (duplicates.length > 0) {
                newErrors.components = 'Component names must be unique';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Check if in edit mode and salary components have changed
        if (mode === 'edit' && employeeCount > 0) {
            const componentsChanged = JSON.stringify(formData.salary_components) !==
                                     JSON.stringify(payGroup.salary_components);

            if (componentsChanged && !showCascadeDialog) {
                // Show cascade confirmation dialog
                setShowCascadeDialog(true);
                return;
            }
        }

        try {
            setSaving(true);

            const payload = {
                ...formData,
                min_ctc: parseFloat(formData.min_ctc),
                max_ctc: parseFloat(formData.max_ctc)
            };

            if (mode === 'create') {
                await payGroupsService.createPayGroup(payload);
            } else {
                await payGroupsService.updatePayGroup(payGroup.id, payload);

                // If cascade update is enabled, call cascade endpoint
                if (cascadeUpdate && employeeCount > 0) {
                    try {
                        const cascadeResult = await payGroupsService.cascadePayGroupUpdate(payGroup.id);
                        console.log('Cascade update result:', cascadeResult.data);

                        if (cascadeResult.data.success) {
                            alert(`Pay group updated successfully! ${cascadeResult.data.updated_count} of ${cascadeResult.data.total_count} employees updated.`);
                        }
                    } catch (cascadeError) {
                        console.error('Cascade update error:', cascadeError);
                        alert('Pay group updated, but some employees could not be updated. Check console for details.');
                    }
                }
            }

            onSave();
        } catch (error) {
            console.error('Error saving pay group:', error);
            alert(error.response?.data?.error || 'Failed to save pay group');
        } finally {
            setSaving(false);
            setShowCascadeDialog(false);
            setCascadeUpdate(false);
        }
    };

    const handleAddComponent = () => {
        setFormData({
            ...formData,
            salary_components: [
                ...formData.salary_components,
                {
                    name: '',
                    percentage: 0,
                    type: 'earning',
                    category: 'fixed'
                }
            ]
        });
    };

    const handleRemoveComponent = (index) => {
        const updated = formData.salary_components.filter((_, i) => i !== index);
        setFormData({ ...formData, salary_components: updated });
    };

    const handleComponentChange = (index, field, value) => {
        const updated = [...formData.salary_components];
        updated[index] = { ...updated[index], [field]: value };
        setFormData({ ...formData, salary_components: updated });
    };

    const getTotalPercentage = () => {
        return formData.salary_components.reduce(
            (sum, comp) => sum + parseFloat(comp.percentage || 0),
            0
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden my-8">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {mode === 'create' ? 'Create Pay Group' : 'Edit Pay Group'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
                    <div className="p-6 space-y-6">
                        {/* Basic Information */}
                        <div>
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                Basic Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                        Pay Group Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500
                                            dark:bg-gray-700 dark:text-white dark:border-gray-600
                                            ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="e.g., L1 - Junior Level"
                                    />
                                    {errors.name && (
                                        <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                        Level *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.level}
                                        onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500
                                            dark:bg-gray-700 dark:text-white dark:border-gray-600
                                            ${errors.level ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="e.g., L1, L2, Manager"
                                    />
                                    {errors.level && (
                                        <p className="text-sm text-red-600 mt-1">{errors.level}</p>
                                    )}
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        rows={2}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500
                                            dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                        placeholder="Brief description of this pay group..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Salary Band */}
                        <div>
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                Salary Band
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                        Minimum CTC *
                                    </label>
                                    <div className="relative">
                                        <DollarSign className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                                        <input
                                            type="number"
                                            value={formData.min_ctc}
                                            onChange={(e) => setFormData({ ...formData, min_ctc: e.target.value })}
                                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500
                                                dark:bg-gray-700 dark:text-white dark:border-gray-600
                                                ${errors.min_ctc || errors.ctc_range ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="300000"
                                            step="1000"
                                        />
                                    </div>
                                    {errors.min_ctc && (
                                        <p className="text-sm text-red-600 mt-1">{errors.min_ctc}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                        Maximum CTC *
                                    </label>
                                    <div className="relative">
                                        <DollarSign className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                                        <input
                                            type="number"
                                            value={formData.max_ctc}
                                            onChange={(e) => setFormData({ ...formData, max_ctc: e.target.value })}
                                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500
                                                dark:bg-gray-700 dark:text-white dark:border-gray-600
                                                ${errors.max_ctc || errors.ctc_range ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="500000"
                                            step="1000"
                                        />
                                    </div>
                                    {errors.max_ctc && (
                                        <p className="text-sm text-red-600 mt-1">{errors.max_ctc}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                        Currency
                                    </label>
                                    <select
                                        value={formData.currency}
                                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500
                                            dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                    >
                                        <option value="INR">INR</option>
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                    </select>
                                </div>
                            </div>
                            {errors.ctc_range && (
                                <p className="text-sm text-red-600 mt-2">{errors.ctc_range}</p>
                            )}
                        </div>

                        {/* Salary Components */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Salary Components
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Define the salary structure template (percentages must total 100%)
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAddComponent}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Component
                                </button>
                            </div>

                            {formData.salary_components.length === 0 ? (
                                <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <Percent className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                                    <p className="text-gray-500 dark:text-gray-400">
                                        No salary components added yet
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {formData.salary_components.map((comp, index) => (
                                        <div
                                            key={index}
                                            className="flex gap-3 items-start p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                                        >
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                                                <input
                                                    type="text"
                                                    value={comp.name}
                                                    onChange={(e) => handleComponentChange(index, 'name', e.target.value)}
                                                    placeholder="Component Name"
                                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                                        focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                                                />

                                                <div className="relative">
                                                    <Percent className="w-4 h-4 absolute right-3 top-2.5 text-gray-400" />
                                                    <input
                                                        type="number"
                                                        value={comp.percentage}
                                                        onChange={(e) => handleComponentChange(index, 'percentage', e.target.value)}
                                                        placeholder="Percentage"
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg
                                                            focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                                                    />
                                                </div>

                                                <select
                                                    value={comp.type}
                                                    onChange={(e) => handleComponentChange(index, 'type', e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                                        focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                                                >
                                                    <option value="earning">Earning</option>
                                                    <option value="deduction">Deduction</option>
                                                </select>

                                                <select
                                                    value={comp.category}
                                                    onChange={(e) => handleComponentChange(index, 'category', e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                                        focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                                                >
                                                    <option value="fixed">Fixed</option>
                                                    <option value="variable">Variable</option>
                                                </select>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => handleRemoveComponent(index)}
                                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    ))}

                                    {/* Total Percentage */}
                                    <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                                        Math.abs(getTotalPercentage() - 100) < 0.01
                                            ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                                            : 'bg-orange-50 dark:bg-orange-900/20 border-orange-500'
                                    }`}>
                                        <span className="font-semibold text-gray-700 dark:text-gray-300">
                                            Total Percentage
                                        </span>
                                        <span className={`text-xl font-bold ${
                                            Math.abs(getTotalPercentage() - 100) < 0.01
                                                ? 'text-green-700 dark:text-green-400'
                                                : 'text-orange-700 dark:text-orange-400'
                                        }`}>
                                            {getTotalPercentage().toFixed(1)}%
                                        </span>
                                    </div>

                                    {errors.components && (
                                        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
                                            <AlertCircle className="w-5 h-5" />
                                            <span className="text-sm">{errors.components}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Status */}
                        <div>
                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Active (employees can be assigned to this pay group)
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? 'Saving...' : mode === 'create' ? 'Create Pay Group' : 'Update Pay Group'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Cascade Confirmation Dialog */}
            {showCascadeDialog && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10 rounded-lg">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4 shadow-xl">
                        <div className="mb-4">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                Update Employee Salary Components?
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                This pay group has <strong>{employeeCount}</strong> assigned {employeeCount === 1 ? 'employee' : 'employees'}.
                            </p>
                        </div>

                        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-yellow-800 dark:text-yellow-300">
                                    <p className="font-semibold mb-1">Important:</p>
                                    <ul className="space-y-1 list-disc list-inside">
                                        <li>Updating will recalculate salary components for all employees</li>
                                        <li>Custom component modifications will be overridden</li>
                                        <li>Changes take effect immediately</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={cascadeUpdate}
                                    onChange={(e) => setCascadeUpdate(e.target.checked)}
                                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 mt-0.5"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Yes, update salary components for all {employeeCount} {employeeCount === 1 ? 'employee' : 'employees'} in this pay group
                                </span>
                            </label>
                        </div>

                        <div className="flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCascadeDialog(false);
                                    setCascadeUpdate(false);
                                }}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={saving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? 'Updating...' : 'Confirm & Update'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PayGroupModal;
